import InfiniteScroll from 'react-infinite-scroll-component';
import dayjs from 'dayjs';
import Title from '../Title';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getData } from '../../api/dataSlice';
import { useGetAccessMonitoringQuery } from '../../api/graphqlApi';
import { getVehicleKey } from './VehicleStats';
import { isMobile } from 'react-device-detect';

export default function AccessMonitoring(props) {

    const { camera, direction } = props
    const { t } = useTranslation()
    const { timestamp, accessMonitoringCameraId, accessMonitoringVehicleFilter } = useSelector(getData)
    const { data: accessMonitoringData } = useGetAccessMonitoringQuery()

    const cameraId = accessMonitoringCameraId ?? camera?.id
    const fallbackUrl = camera?.image ?? ""
    const numImagesToLoadAtScroll = 10

    const [visibleCount, setVisibleCount] = useState(numImagesToLoadAtScroll)
    const [loadedImages, setLoadedImages] = useState(() => new Set())
    const [failedImages, setFailedImages] = useState(() => new Set())

    const baseUrl = useMemo(() => {
        const download = camera?.download
        if (!download) return "" // prevent getting "undefinedbest_frames/"
        return `${download.replace("zeitraffer", "einfahrt")}best_frames/`
    }, [camera?.download])

    const allEvents = useMemo(() => (
        accessMonitoringData?.byCameraId?.[cameraId] ?? []
    ), [accessMonitoringData, cameraId])

    const selectedDay = useMemo(() => {
        const base = timestamp ? dayjs(timestamp) : dayjs()
        return base.isValid() ? base.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")
    }, [timestamp])

    const vehicleFilterSet = useMemo(
        () => accessMonitoringVehicleFilter ? new Set(accessMonitoringVehicleFilter) : null,
        [accessMonitoringVehicleFilter]
    )

    const dayEvents = useMemo(() => {
        const filtered = allEvents
            .filter(elem => typeof elem?.timestamp === "string")
            .filter(elem => elem?.timestamp?.slice(0, 10) === selectedDay) // faster than dayjs comparison
            .filter(elem => elem?.direction === direction)
            .filter(elem => !vehicleFilterSet || vehicleFilterSet.has(getVehicleKey(elem)))
            .filter(elem => !failedImages.has(elem.id))

        return filtered.slice().sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)) // newest first (descending)
    }, [allEvents, selectedDay, direction, vehicleFilterSet, failedImages])

    const visibleEvents = useMemo(() => (
        dayEvents.slice(0, visibleCount)
    ), [dayEvents, visibleCount])

    const pendingVisibleCount = useMemo(() => {
        let notLoaded = 0
        for (const elem of visibleEvents) {
            if (!loadedImages.has(elem.id)) notLoaded++
        }
        return notLoaded
    }, [visibleEvents, loadedImages])

    const setStateWithoutDoubles = useCallback((setState) => (id) => {
        setState(prev => {
            if (prev.has(id)) return prev
            const next = new Set(prev)
            next.add(id)
            return next
        })
    }, [])

    const markLoaded = useMemo(() => setStateWithoutDoubles(setLoadedImages), [setStateWithoutDoubles])
    const markFailed = useMemo(() => setStateWithoutDoubles(setFailedImages), [setStateWithoutDoubles])

    const getVehicleLabel = useCallback((event) => {
        const subtype = event?.vehicleSubtype?.name
        if (subtype) return t(`pages.access.vehicle.subtypes.${subtype}`, subtype)

        const type = event?.vehicleType?.name
        if (type) return t(`pages.access.vehicle.types.${type}`, type)

        return ""
    }, [t]) 

    const hasMoreValue = visibleCount < dayEvents.length

    const handleOnRowsScrollEnd = useCallback(() => {
        if (!hasMoreValue) return
        setVisibleCount(prev => Math.min(prev + numImagesToLoadAtScroll, dayEvents.length))
    }, [hasMoreValue, dayEvents.length])

    // reset tracker if day/direction/camera changes
    useEffect(() => {
        setVisibleCount(numImagesToLoadAtScroll)
        setLoadedImages(new Set())
        setFailedImages(new Set())
    }, [selectedDay, direction, cameraId])

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            alignItems: 'center',
            minHeight: 0
        }}>

            <Title cameraName={direction === "entry" ? t("pages.access.entry") : t("pages.access.exit")} />

            <Box
                id={`infinite-scroll-${direction}`}
                className="thin-scrollbar"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    mt: "36px",
                    mr: isMobile ? 0.5 : 1
                }}
            >

                {/* prevent hiding scrollbar */}

                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    pt: 0.5,
                    pl: 0.5,
                    pr: isMobile ? 0 : 0.5
                }}> 

                    <InfiniteScroll
                        dataLength={visibleEvents.length}
                        next={handleOnRowsScrollEnd}
                        hasMore={hasMoreValue}
                        scrollThreshold={0.8}
                        scrollableTarget={`infinite-scroll-${direction}`}
                        loader={null}
                        style={{ overflow: 'unset' }} // allow sticky headers or proper layout if needed
                    >

                        {visibleEvents.map((elem) => {
                            const filename = elem.bestImageName
                            const imgSrc = filename ? `${baseUrl}${filename}` : fallbackUrl
                            const isLoaded = loadedImages.has(elem.id)
                            const vehicleLabel = getVehicleLabel(elem)

                            return (
                                <Box
                                    key={elem.id}
                                    sx={{
                                        height: isLoaded ? "auto" : 0, // hide before loaded
                                        overflow: "hidden",
                                        mb: isLoaded ? 0.5 : 0,
                                        backgroundColor: isLoaded ? 'white' : 'transparent',
                                        borderBottomLeftRadius: isLoaded ? 6 : 0,
                                        borderBottomRightRadius: isLoaded ? 6 : 0
                                    }}
                                >

                                    <img
                                        src={imgSrc}
                                        alt={filename || "image"}
                                        style={{
                                            display: "flex",
                                            width: "100%",
                                            objectFit: "cover"
                                        }}
                                        loading="lazy"
                                        ref={img => {
                                            if (img && img.complete && img.naturalWidth > 0) {
                                                markLoaded(elem.id)
                                            }
                                        }}
                                        onLoad={() => markLoaded(elem.id)}
                                        onError={() => markFailed(elem.id)}
                                    />

                                     {isLoaded && (
                                        <Typography
                                            variant="body1"
                                            align="center"
                                            sx={{ mt: 0.5, userSelect: "none" }}
                                        >
                                            {vehicleLabel ? `${vehicleLabel} | ` : ""}
                                            {dayjs(elem.timestamp).format(t("date.format.full_date_time"))}
                                        </Typography>
                                    )}
                                </Box>
                            )
                        })}
                    </InfiniteScroll>

                    {dayEvents.length > 0 && pendingVisibleCount > 0 && (
                        <LinearProgress sx={{ mt: 0.5, mb: 1.5 }} />
                    )}

                    {accessMonitoringData && dayEvents.length === 0 && (
                        <Box sx={{
                            minHeight: isMobile ? "auto" : "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pt: isMobile ? 0.5 : 0,
                            pb: isMobile ? 1 : 0
                        }}>
                            <Typography sx={{ color: 'white', userSelect: 'none' }}>
                                {t("msg.no_data")}
                            </Typography>
                        </Box>
                    )}

                </Box>
            </Box>

        </Box>
    )
}
